import React, { useState, useEffect } from "react";
import { db, storage } from "../../firebase";
import {
    collection, getDocs, addDoc, updateDoc, deleteDoc,
    doc, serverTimestamp, onSnapshot
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { compressImage } from "../../utils/imageUtils";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { 
    FiPlus, FiEdit2, FiTrash2, FiX, FiUpload, FiLayout, 
    FiBook, FiChevronRight, FiChevronLeft, FiPlusCircle, FiUsers, FiCompass, FiFileText
} from "react-icons/fi";
import toast from "react-hot-toast";

const BOOK_STYLES = [
    { id: "classic", label: "Classic Leather" },
    { id: "golden", label: "Imperial Gold Filigree" },
    { id: "gothic", label: "Gothic Silver Crest" },
    { id: "cosmic", label: "Celestial Nebula" }
];

const LORE_TYPES = [
    { id: "character", label: "Character", color: "#d97706", icon: FiUsers },
    { id: "lore", label: "Lore Element (Magic, Faction, Location)", color: "#0d9488", icon: FiCompass },
    { id: "note", label: "General Note / Plot Element", color: "#4f46e5", icon: FiFileText }
];

const quillModules = {
    toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet'}],
        ['clean']
    ],
};

export default function AdminCanvas() {
    // Sagas List state
    const [sagas, setSagas] = useState([]);
    const [loadingSagas, setLoadingSagas] = useState(true);
    const [selectedSaga, setSelectedSaga] = useState(null); // Saga currently editing lore for
    
    // Lore Nodes state
    const [loreNodes, setLoreNodes] = useState([]);
    const [loadingLore, setLoadingLore] = useState(false);

    // Modals & forms state
    const [showSagaModal, setShowSagaModal] = useState(false);
    const [editingSagaId, setEditingSagaId] = useState(null);
    const [sagaForm, setSagaForm] = useState({
        title: "", description: "", bookColor: "#8b1e0f", bookStyle: "classic", imageUrl: "", x: 50, y: 50
    });
    
    const [showNodeModal, setShowNodeModal] = useState(false);
    const [editingNodeId, setEditingNodeId] = useState(null);
    const [nodeForm, setNodeForm] = useState({
        title: "", type: "character", content: "", book1Content: "", book2Content: "", imageUrl: "", x: 50, y: 50, connections: [],
        age: "", characterRole: "Protagonist", characterClass: "Human", physical: "", personality: "",
        imagePosition: "center center", isSpoiler: false,
        contentIsSpoiler: false, book1IsSpoiler: false, book2IsSpoiler: false
    });
    const [activeEditorTab, setActiveEditorTab] = useState("standard");

    const [uploading, setUploading] = useState(false);
    const [draggingNode, setDraggingNode] = useState(null); // { id, dragType, startX, startY, initialX, initialY, container }
    const [zoomScale, setZoomScale] = useState(0.45); // 45% on 1600x1200 shows the full layout comfortably
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [canvasPanState, setCanvasPanState] = useState({ isPanning: false, startX: 0, startY: 0, panX: 0, panY: 0 });

    // Whiteboard Canvas Annotations States
    const [decorations, setDecorations] = useState([]);
    const [selectedDecoration, setSelectedDecoration] = useState(null);
    const [drawingMode, setDrawingMode] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [currentScribblePoints, setCurrentScribblePoints] = useState([]);
    const [strokeColor, setStrokeColor] = useState("#ff0055");
    const [strokeWidth, setStrokeWidth] = useState(3);
    const [showBgConfig, setShowBgConfig] = useState(false);
    const [bgForm, setBgForm] = useState({
        bgImageUrl: "",
        bgImagePosition: "center center",
        bgOverlayColor: "#000000",
        bgOverlayOpacity: 0.6
    });

    const adminBoardRef = React.useRef(null);
    const adminCanvasContainerRef = React.useRef(null);
    const [sidebarTab, setSidebarTab] = useState("cards"); // "cards", "annotations"

    // Center scroll on load when a saga is selected
    useEffect(() => {
        if (selectedSaga) {
            setTimeout(() => {
                if (adminBoardRef.current) {
                    const cW = adminBoardRef.current.clientWidth;
                    const cH = adminBoardRef.current.clientHeight;
                    setPan({
                        x: cW / 2 - 800 * 0.45,
                        y: cH / 2 - 600 * 0.45
                    });
                }
            }, 150);
        }
    }, [selectedSaga]);

    // Mouse-wheel zoom on admin canvas (non-passive so we can preventDefault)
    useEffect(() => {
        const el = adminCanvasContainerRef.current;
        if (!el) return;
        const handler = (e) => {
            e.preventDefault();
            setZoomScale(prev => {
                const delta = e.deltaY > 0 ? -0.04 : 0.04;
                const next = Math.min(2.0, Math.max(0.15, parseFloat((prev + delta).toFixed(2))));
                // Zoom towards mouse cursor
                if (adminBoardRef.current) {
                    const rect = adminBoardRef.current.getBoundingClientRect();
                    const mx = e.clientX - rect.left;
                    const my = e.clientY - rect.top;
                    setPan(currentPan => {
                        const cx = (mx - currentPan.x) / prev;
                        const cy = (my - currentPan.y) / prev;
                        return {
                            x: mx - cx * next,
                            y: my - cy * next
                        };
                    });
                }
                return next;
            });
        };
        el.addEventListener('wheel', handler, { passive: false });
        return () => el.removeEventListener('wheel', handler);
    }, [selectedSaga]);

    // Load Background Form when saga is selected
    useEffect(() => {
        if (selectedSaga) {
            setBgForm({
                bgImageUrl: selectedSaga.bgImageUrl || "",
                bgImagePosition: selectedSaga.bgImagePosition || "center center",
                bgOverlayColor: selectedSaga.bgOverlayColor || "#000000",
                bgOverlayOpacity: selectedSaga.bgOverlayOpacity ?? 0.6
            });
            setShowBgConfig(false);
            setSelectedDecoration(null);
            setDrawingMode(false);
        }
    }, [selectedSaga]);

    // Load Decorations
    useEffect(() => {
        if (!selectedSaga) {
            setDecorations([]);
            return;
        }
        const unsub = onSnapshot(collection(db, "sagas", selectedSaga.id, "decorations"), (snap) => {
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setDecorations(list);
        }, (err) => {
            console.error("Failed to load decorations:", err);
        });
        return () => unsub();
    }, [selectedSaga]);

    // Load Sagas
    useEffect(() => {
        const unsub = onSnapshot(collection(db, "sagas"), (snap) => {
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setSagas(list);
            setLoadingSagas(false);
        }, (err) => {
            toast.error("Failed to load sagas.");
            setLoadingSagas(false);
        });
        return () => unsub();
    }, []);

    // Load Lore Nodes when selected saga changes
    useEffect(() => {
        if (!selectedSaga) {
            setLoreNodes([]);
            return;
        }
        setLoadingLore(true);
        const unsub = onSnapshot(collection(db, "sagas", selectedSaga.id, "lore"), (snap) => {
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setLoreNodes(list);
            setLoadingLore(false);
        }, (err) => {
            toast.error("Failed to load lore nodes.");
            setLoadingLore(false);
        });
        return () => unsub();
    }, [selectedSaga]);

    // Proximity/Overlap Nudging helper
    const resolveOverlap = (id, newX, newY, nodesList) => {
        let resolvedX = newX;
        let resolvedY = newY;
        let attempts = 0;
        const paddingX = 16.0; // ~256px spacing
        const paddingY = 23.0; // ~276px spacing
        
        let hasOverlap = true;
        while (hasOverlap && attempts < 15) {
            hasOverlap = false;
            for (const item of nodesList) {
                if (item.id === id) continue;
                const dx = Math.abs(resolvedX - item.x);
                const dy = Math.abs(resolvedY - item.y);
                if (dx < paddingX && dy < paddingY) {
                    // Overlap detected! Shift right
                    resolvedX += paddingX;
                    if (resolvedX > 90) { // Wrap to next row
                        resolvedX = newX;
                        resolvedY += paddingY;
                    }
                    hasOverlap = true;
                    break;
                }
            }
            attempts++;
        }
        return { 
            x: Math.min(99, Math.max(0.5, Math.round(resolvedX * 10) / 10)), 
            y: Math.min(99, Math.max(0.5, Math.round(resolvedY * 10) / 10)) 
        };
    };

    // Handle Dragging
    const handleMouseDown = (e, id, dragType, initialX, initialY) => {
        e.preventDefault();
        e.stopPropagation();
        const container = e.currentTarget.parentElement.getBoundingClientRect();
        setDraggingNode({
            id,
            dragType,
            container,
            initialX,
            initialY,
            startX: e.clientX,
            startY: e.clientY
        });
    };

    const handleMouseMove = (e) => {
        if (!draggingNode) return;
        const deltaX = e.clientX - draggingNode.startX;
        const deltaY = e.clientY - draggingNode.startY;
        
        const currentScale = draggingNode.dragType === "saga" ? 1.0 : zoomScale;
        const pctDeltaX = (deltaX / (draggingNode.container.width / currentScale)) * 100;
        const pctDeltaY = (deltaY / (draggingNode.container.height / currentScale)) * 100;
        
        let newX = Math.min(99.5, Math.max(0, draggingNode.initialX + pctDeltaX));
        let newY = Math.min(99.5, Math.max(0, draggingNode.initialY + pctDeltaY));
        
        newX = Math.round(newX * 10) / 10;
        newY = Math.round(newY * 10) / 10;

        if (draggingNode.dragType === "saga") {
            setSagas(prev => prev.map(s => s.id === draggingNode.id ? { ...s, x: newX, y: newY } : s));
        } else if (draggingNode.dragType === "node") {
            setLoreNodes(prev => prev.map(n => n.id === draggingNode.id ? { ...n, x: newX, y: newY } : n));
        } else if (draggingNode.dragType === "decoration") {
            setDecorations(prev => prev.map(d => d.id === draggingNode.id ? { ...d, x: newX, y: newY } : d));
        } else if (draggingNode.dragType === "decorationEnd") {
            setDecorations(prev => prev.map(d => d.id === draggingNode.id ? { ...d, x2: newX, y2: newY } : d));
        }
    };

    const handleMouseUp = async () => {
        if (!draggingNode) return;
        const { id, dragType } = draggingNode;
        
        try {
            if (dragType === "saga") {
                const item = sagas.find(s => s.id === id);
                if (item) {
                    await updateDoc(doc(db, "sagas", id), { x: item.x, y: item.y });
                }
            } else if (dragType === "node") {
                const item = loreNodes.find(n => n.id === id);
                if (item) {
                    const nudged = resolveOverlap(id, item.x, item.y, loreNodes);
                    setLoreNodes(prev => prev.map(n => n.id === id ? { ...n, x: nudged.x, y: nudged.y } : n));
                    await updateDoc(doc(db, "sagas", selectedSaga.id, "lore", id), { x: nudged.x, y: nudged.y });
                }
            } else if (dragType === "decoration") {
                const item = decorations.find(d => d.id === id);
                if (item) {
                    await updateDoc(doc(db, "sagas", selectedSaga.id, "decorations", id), { x: item.x, y: item.y });
                }
            } else if (dragType === "decorationEnd") {
                const item = decorations.find(d => d.id === id);
                if (item) {
                    await updateDoc(doc(db, "sagas", selectedSaga.id, "decorations", id), { x2: item.x2, y2: item.y2 });
                }
            }
        } catch (err) {
            console.error("Save position error:", err);
        }
        setDraggingNode(null);
    };

    useEffect(() => {
        if (draggingNode) {
            window.addEventListener("mousemove", handleMouseMove);
            window.addEventListener("mouseup", handleMouseUp);
        }
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [draggingNode, sagas, loreNodes, decorations]);

    // Canvas drag-to-pan handlers
    const handleCanvasMouseDown = (e) => {
        // Do not trigger pan if in drawing/scribble mode
        if (drawingMode) return;
        // Do not trigger pan if clicking on an interactive control button or sidebar cards/annotation forms
        if (e.target.closest("button") || e.target.closest(".admin-card") || e.target.closest(".admin-modal")) return;
        
        setCanvasPanState({
            isPanning: true,
            startX: e.clientX,
            startY: e.clientY,
            panX: pan.x,
            panY: pan.y
        });
    };

    useEffect(() => {
        if (!canvasPanState.isPanning) return;

        const handleCanvasMouseMoveGlobal = (e) => {
            const dx = e.clientX - canvasPanState.startX;
            const dy = e.clientY - canvasPanState.startY;
            setPan({
                x: canvasPanState.panX + dx,
                y: canvasPanState.panY + dy
            });
        };

        const handleCanvasMouseUpGlobal = () => {
            setCanvasPanState(prev => ({ ...prev, isPanning: false }));
        };

        window.addEventListener("mousemove", handleCanvasMouseMoveGlobal);
        window.addEventListener("mouseup", handleCanvasMouseUpGlobal);
        return () => {
            window.removeEventListener("mousemove", handleCanvasMouseMoveGlobal);
            window.removeEventListener("mouseup", handleCanvasMouseUpGlobal);
        };
    }, [canvasPanState.isPanning, canvasPanState.startX, canvasPanState.startY, canvasPanState.panX, canvasPanState.panY]);

    const zoomAdminCanvas = (direction) => {
        const delta = direction === "in" ? 0.08 : -0.08;
        setZoomScale(prev => {
            const next = Math.min(2.0, Math.max(0.15, parseFloat((prev + delta).toFixed(2))));
            if (adminBoardRef.current) {
                const rect = adminBoardRef.current.getBoundingClientRect();
                const mx = rect.width / 2;
                const my = rect.height / 2;
                setPan(currentPan => {
                    const cx = (mx - currentPan.x) / prev;
                    const cy = (my - currentPan.y) / prev;
                    return {
                        x: mx - cx * next,
                        y: my - cy * next
                    };
                });
            }
            return next;
        });
    };

    // Image Upload helper
    async function uploadImage(e, callback) {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const compressed = await compressImage(file);
            const storageRef = ref(storage, `lore/${Date.now()}_${compressed.name}`);
            const uploadTask = uploadBytesResumable(storageRef, compressed);
            uploadTask.on("state_changed", null, 
                () => { toast.error("Upload failed."); setUploading(false); },
                async () => {
                    const url = await getDownloadURL(uploadTask.snapshot.ref);
                    callback(url);
                    setUploading(false);
                    toast.success("Image uploaded!");
                }
            );
        } catch (err) {
            console.error(err);
            toast.error("Upload failed.");
            setUploading(false);
        }
    }

    // Saga Handlers
    function startAddSaga() {
        setSagaForm({ title: "", description: "", bookColor: "#8b1e0f", bookStyle: "classic", imageUrl: "", x: 50, y: 50 });
        setEditingSagaId(null);
        setShowSagaModal(true);
    }

    function startEditSaga(s) {
        setSagaForm({
            title: s.title || "",
            description: s.description || "",
            bookColor: s.bookColor || "#8b1e0f",
            bookStyle: s.bookStyle || "classic",
            imageUrl: s.imageUrl || "",
            x: s.x ?? 50,
            y: s.y ?? 50
        });
        setEditingSagaId(s.id);
        setShowSagaModal(true);
    }

    async function saveSaga() {
        if (!sagaForm.title.trim()) {
            toast.error("Title is required.");
            return;
        }
        try {
            if (editingSagaId) {
                await updateDoc(doc(db, "sagas", editingSagaId), sagaForm);
                toast.success("Saga updated!");
            } else {
                await addDoc(collection(db, "sagas"), {
                    ...sagaForm,
                    createdAt: serverTimestamp()
                });
                toast.success("Saga created!");
            }
            setShowSagaModal(false);
        } catch {
            toast.error("Failed to save saga.");
        }
    }

    async function deleteSaga(id) {
        if (!window.confirm("Delete this saga and all its lore? This cannot be undone.")) return;
        try {
            // First delete lore subcollection nodes
            const loreSnap = await getDocs(collection(db, "sagas", id, "lore"));
            const deletePromises = loreSnap.docs.map(d => deleteDoc(doc(db, "sagas", id, "lore", d.id)));
            await Promise.all(deletePromises);
            
            // Delete saga document
            await deleteDoc(doc(db, "sagas", id));
            toast.success("Saga deleted.");
            if (selectedSaga?.id === id) setSelectedSaga(null);
        } catch {
            toast.error("Failed to delete saga.");
        }
    }

    // Lore Node Handlers
    function startAddNode() {
        setNodeForm({ 
            title: "", type: "character", content: "", book1Content: "", book2Content: "", imageUrl: "", x: 50, y: 50, connections: [],
            age: "", characterRole: "Protagonist", characterClass: "Human", physical: "", personality: "",
            imagePosition: "center center", isSpoiler: false,
            contentIsSpoiler: false, book1IsSpoiler: false, book2IsSpoiler: false
        });
        setActiveEditorTab("standard");
        setEditingNodeId(null);
        setShowNodeModal(true);
    }

    function startEditNode(n) {
        setNodeForm({
            title: n.title || "",
            type: n.type || "character",
            content: n.content || "",
            book1Content: n.book1Content || "",
            book2Content: n.book2Content || "",
            imageUrl: n.imageUrl || "",
            x: n.x ?? 50,
            y: n.y ?? 50,
            connections: n.connections || [],
            age: n.age || "",
            characterRole: n.characterRole || n.characterType || "Protagonist",
            characterClass: n.characterClass || "Human",
            physical: n.physical || "",
            personality: n.personality || "",
            imagePosition: n.imagePosition || "center center",
            isSpoiler: n.isSpoiler || false,
            contentIsSpoiler: n.contentIsSpoiler || false,
            book1IsSpoiler: n.book1IsSpoiler || false,
            book2IsSpoiler: n.book2IsSpoiler || false
        });
        setActiveEditorTab("standard");
        setEditingNodeId(n.id);
        setShowNodeModal(true);
    }

    async function saveNode() {
        if (!nodeForm.title.trim()) {
            toast.error("Title is required.");
            return;
        }
        try {
            const data = { ...nodeForm };
            if (editingNodeId) {
                await updateDoc(doc(db, "sagas", selectedSaga.id, "lore", editingNodeId), data);
                
                // Clean up removed (unlinked) bilateral connections
                const prevNode = loreNodes.find(n => n.id === editingNodeId);
                if (prevNode) {
                    const prevConns = prevNode.connections || [];
                    const removedConns = prevConns.filter(c => !data.connections.includes(c));
                    for (const connId of removedConns) {
                        const connDoc = doc(db, "sagas", selectedSaga.id, "lore", connId);
                        const connNode = loreNodes.find(n => n.id === connId);
                        if (connNode) {
                            const updatedConns = (connNode.connections || []).filter(c => c !== editingNodeId);
                            await updateDoc(connDoc, { connections: updatedConns });
                        }
                    }
                }

                // Add bilateral connections if connection toggled
                for (const connId of data.connections) {
                    const connDoc = doc(db, "sagas", selectedSaga.id, "lore", connId);
                    const connNode = loreNodes.find(n => n.id === connId);
                    if (connNode && !connNode.connections?.includes(editingNodeId)) {
                        await updateDoc(connDoc, {
                            connections: [...(connNode.connections || []), editingNodeId]
                        });
                    }
                }
                
                toast.success("Lore node updated!");
            } else {
                const docRef = await addDoc(collection(db, "sagas", selectedSaga.id, "lore"), {
                    ...data,
                    createdAt: serverTimestamp()
                });
                
                // Add bilateral connections
                for (const connId of data.connections) {
                    const connDoc = doc(db, "sagas", selectedSaga.id, "lore", connId);
                    const connNode = loreNodes.find(n => n.id === connId);
                    if (connNode && !connNode.connections?.includes(docRef.id)) {
                        await updateDoc(connDoc, {
                            connections: [...(connNode.connections || []), docRef.id]
                        });
                    }
                }
                toast.success("Lore node created!");
            }
            setShowNodeModal(false);
        } catch (err) {
            console.error(err);
            toast.error("Failed to save lore node.");
        }
    }

    async function deleteNode(id) {
        if (!window.confirm("Remove this lore card?")) return;
        try {
            // Clean up connections pointing to this node from other nodes
            const connected = loreNodes.filter(n => n.connections?.includes(id));
            const cleanupPromises = connected.map(n => {
                const nextConns = n.connections.filter(c => c !== id);
                return updateDoc(doc(db, "sagas", selectedSaga.id, "lore", n.id), { connections: nextConns });
            });
            await Promise.all(cleanupPromises);

            await deleteDoc(doc(db, "sagas", selectedSaga.id, "lore", id));
            toast.success("Lore node deleted.");
        } catch {
            toast.error("Failed to delete lore node.");
        }
    }

    function toggleConnection(id) {
        setNodeForm(prev => {
            const connections = prev.connections.includes(id)
                ? prev.connections.filter(c => c !== id)
                : [...prev.connections, id];
            return { ...prev, connections };
        });
    }

    // Whiteboard Canvas Actions
    const saveBgConfig = async () => {
        try {
            await updateDoc(doc(db, "sagas", selectedSaga.id), {
                bgImageUrl: bgForm.bgImageUrl,
                bgImagePosition: bgForm.bgImagePosition,
                bgOverlayColor: bgForm.bgOverlayColor,
                bgOverlayOpacity: parseFloat(bgForm.bgOverlayOpacity)
            });
            toast.success("Background settings updated!");
            setShowBgConfig(false);
        } catch {
            toast.error("Failed to save background settings.");
        }
    };

    const getSpawnCoords = () => {
        const offset = (decorations.length % 5) * 5;
        return { x: 45 + offset, y: 45 + offset };
    };

    const addTitleDecoration = async () => {
        try {
            const coords = getSpawnCoords();
            await addDoc(collection(db, "sagas", selectedSaga.id, "decorations"), {
                type: "title",
                text: "New Label",
                fontSize: 24,
                color: "#ffffff",
                x: coords.x,
                y: coords.y,
                createdAt: serverTimestamp()
            });
            toast.success("Added title label!");
        } catch (err) {
            toast.error("Failed to add label.");
        }
    };

    const addShapeDecoration = async (shapeType) => {
        try {
            const coords = getSpawnCoords();
            await addDoc(collection(db, "sagas", selectedSaga.id, "decorations"), {
                type: "shape",
                shapeType: shapeType,
                width: 15,
                height: 10,
                color: "#38bdf8",
                x: coords.x,
                y: coords.y,
                createdAt: serverTimestamp()
            });
            toast.success(`Added ${shapeType}!`);
        } catch (err) {
            toast.error("Failed to add shape.");
        }
    };

    const addLineDecoration = async () => {
        try {
            const coords = getSpawnCoords();
            await addDoc(collection(db, "sagas", selectedSaga.id, "decorations"), {
                type: "line",
                x: coords.x - 10,
                y: coords.y,
                x2: coords.x + 10,
                y2: coords.y,
                color: "#fbbf24",
                strokeWidth: 3,
                dashed: false,
                createdAt: serverTimestamp()
            });
            toast.success("Added line!");
        } catch (err) {
            toast.error("Failed to add line.");
        }
    };

    const addImageDecoration = async () => {
        try {
            const coords = getSpawnCoords();
            await addDoc(collection(db, "sagas", selectedSaga.id, "decorations"), {
                type: "image",
                imageUrl: "",
                imagePosition: "center center",
                width: 20,
                height: 15,
                x: coords.x,
                y: coords.y,
                createdAt: serverTimestamp()
            });
            toast.success("Added image placeholder!");
        } catch (err) {
            toast.error("Failed to add image.");
        }
    };

    const deleteDecoration = async (id) => {
        if (!window.confirm("Delete this decoration?")) return;
        try {
            await deleteDoc(doc(db, "sagas", selectedSaga.id, "decorations", id));
            setSelectedDecoration(null);
            toast.success("Decoration deleted.");
        } catch {
            toast.error("Failed to delete decoration.");
        }
    };

    const saveDecoration = async () => {
        if (!selectedDecoration) return;
        try {
            const dRef = doc(db, "sagas", selectedSaga.id, "decorations", selectedDecoration.id);
            const { id, ...data } = selectedDecoration;
            await updateDoc(dRef, data);
            toast.success("Decoration settings saved!");
            setSelectedDecoration(null);
        } catch (err) {
            console.error("Save decoration error:", err);
            toast.error("Failed to save decoration.");
        }
    };

    const handleDrawingMouseDown = (e) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const pctX = ((e.clientX - rect.left) / rect.width) * 100;
        const pctY = ((e.clientY - rect.top) / rect.height) * 100;
        setIsDrawing(true);
        setCurrentScribblePoints([{ x: pctX, y: pctY }]);
    };

    const handleDrawingMouseMove = (e) => {
        if (!isDrawing) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const pctX = ((e.clientX - rect.left) / rect.width) * 100;
        const pctY = ((e.clientY - rect.top) / rect.height) * 100;
        setCurrentScribblePoints(prev => [...prev, { x: pctX, y: pctY }]);
    };

    const handleDrawingMouseUp = async () => {
        if (!isDrawing) return;
        setIsDrawing(false);
        if (currentScribblePoints.length > 2) {
            try {
                await addDoc(collection(db, "sagas", selectedSaga.id, "decorations"), {
                    type: "scribble",
                    points: currentScribblePoints,
                    color: strokeColor,
                    strokeWidth: strokeWidth,
                    x: 0,
                    y: 0,
                    createdAt: serverTimestamp()
                });
            } catch (err) {
                toast.error("Failed to save sketch.");
            }
        }
        setCurrentScribblePoints([]);
    };

    if (selectedSaga) {
        // Inner Lore Board Manager View
        return (
            <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                    <button className="admin-btn-secondary" onClick={() => setSelectedSaga(null)} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <FiChevronLeft size={16} /> Back to Sagas
                    </button>
                    <h3 style={{ margin: 0 }}>Lore Canvas: {selectedSaga.title}</h3>
                    <button className="admin-btn-primary" onClick={startAddNode} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <FiPlus size={16} /> Add Lore Card
                    </button>
                </div>

                {/* Visual Editor Toolbar */}
                <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 10,
                    padding: "10px 16px",
                    background: "#1e293b",
                    borderRadius: "12px 12px 0 0",
                    border: "1px solid #334155",
                    borderBottom: "none",
                    alignItems: "center"
                }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginRight: 8 }}>Canvas Tools</span>
                    
                    <button className="admin-btn-secondary" style={{ padding: "6px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }} onClick={addTitleDecoration}>
                        <FiFileText size={13} /> + Label
                    </button>
                    <button className="admin-btn-secondary" style={{ padding: "6px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }} onClick={() => addShapeDecoration("rectangle")}>
                        <FiLayout size={13} /> + Rect
                    </button>
                    <button className="admin-btn-secondary" style={{ padding: "6px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }} onClick={() => addShapeDecoration("circle")}>
                        <FiCompass size={13} /> + Circle
                    </button>
                    <button className="admin-btn-secondary" style={{ padding: "6px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }} onClick={addLineDecoration}>
                        <FiChevronRight size={13} /> + Line
                    </button>
                    <button className="admin-btn-secondary" style={{ padding: "6px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }} onClick={addImageDecoration}>
                        <FiUpload size={13} /> + Image
                    </button>
                    <button 
                        className="admin-btn-secondary" 
                        style={{ 
                            padding: "6px 12px", 
                            fontSize: 12, 
                            display: "flex", 
                            alignItems: "center", 
                            gap: 4,
                            background: drawingMode ? "#e11d48" : "",
                            borderColor: drawingMode ? "#be123c" : "",
                            color: drawingMode ? "white" : ""
                        }} 
                        onClick={() => {
                            setDrawingMode(!drawingMode);
                            setSelectedDecoration(null);
                            setShowBgConfig(false);
                        }}
                    >
                        <FiPlusCircle size={13} /> {drawingMode ? "Stop Drawing" : "Freehand Sketch"}
                    </button>
 
                    <button className="admin-btn-secondary" style={{ padding: "6px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 4, marginLeft: "auto" }} onClick={() => { setShowBgConfig(true); setSelectedDecoration(null); }}>
                        <FiBook size={13} /> Canvas Background
                    </button>
                </div>
 
                <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 24, alignItems: "start" }}>
                    {/* Visual Canvas Panel */}
                    <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                            <p style={{ margin: 0, fontSize: 13, color: "#888", fontWeight: 600 }}>Drag cards/decorations to position them. Click annotations to edit.</p>
                            <span style={{ fontSize: 11, background: "#e2e8f0", padding: "4px 8px", borderRadius: 4, fontWeight: 700 }}>LIVE DRAG SAVING</span>
                        </div>
                        
                        <div
                            ref={adminCanvasContainerRef}
                            style={{ 
                                position: "relative", 
                                width: "100%", 
                                height: "580px", 
                                borderRadius: "0 0 16px 16px", 
                                border: "2px dashed #334155",
                                overflow: "hidden",
                                background: "#080b11"
                            }}
                        >
                            {/* Background Backdrop Layer - Fits Container Exactly */}
                            {selectedSaga.bgImageUrl && (
                                <div style={{
                                    position: "absolute",
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundImage: `url("${selectedSaga.bgImageUrl}")`,
                                    backgroundSize: "cover",
                                    backgroundPosition: selectedSaga.bgImagePosition || "center center",
                                    backgroundRepeat: "no-repeat",
                                    zIndex: 0
                                }} />
                            )}
                            {/* Background Color Overlay - Fits Container Exactly */}
                            {selectedSaga.bgImageUrl && (
                                <div style={{
                                    position: "absolute",
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    background: selectedSaga.bgOverlayColor || "#000000",
                                    opacity: selectedSaga.bgOverlayOpacity ?? 0.6,
                                    zIndex: 0,
                                    pointerEvents: "none"
                                }} />
                            )}

                            {/* Floating Zoom Controls - Pinned to the top-right of the viewport */}
                            <div style={{
                                position: "absolute",
                                top: 12,
                                right: 12,
                                background: "rgba(15, 23, 42, 0.9)",
                                border: "1px solid #334155",
                                borderRadius: 8,
                                padding: "4px 8px",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                zIndex: 100,
                                pointerEvents: "auto",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.5)"
                            }} onMouseDown={e => e.stopPropagation()}>
                                <button 
                                    className="admin-btn-secondary" 
                                    style={{ padding: "4px 8px", fontSize: 11, minWidth: 24 }}
                                    onClick={() => zoomAdminCanvas("out")}
                                    title="Zoom Out"
                                >
                                    −
                                </button>
                                <span style={{ fontSize: 11, color: "white", fontWeight: 700, minWidth: 40, textAlign: "center" }}>
                                    {Math.round(zoomScale * 100)}%
                                </span>
                                <button 
                                    className="admin-btn-secondary" 
                                    style={{ padding: "4px 8px", fontSize: 11, minWidth: 24 }}
                                    onClick={() => zoomAdminCanvas("in")}
                                    title="Zoom In"
                                >
                                    +
                                </button>
                                <button 
                                    className="admin-btn-secondary" 
                                    style={{ padding: "4px 8px", fontSize: 11 }}
                                    onClick={() => {
                                        if (!adminBoardRef.current || loreNodes.length === 0) { setZoomScale(0.45); return; }
                                        const cW = adminBoardRef.current.clientWidth;
                                        const cH = adminBoardRef.current.clientHeight;
                                        const xs = loreNodes.map(n => (n.x / 100) * 1600);
                                        const ys = loreNodes.map(n => (n.y / 100) * 1200);
                                        const minX = Math.min(...xs), maxX = Math.max(...xs);
                                        const minY = Math.min(...ys), maxY = Math.max(...ys);
                                        const pad = 120;
                                        const nz = Math.max(0.3, Math.min(cW / (maxX - minX + pad * 2), cH / (maxY - minY + pad * 2), 1.5));
                                        setZoomScale(nz);
                                        const midX = (minX + maxX) / 2;
                                        const midY = (minY + maxY) / 2;
                                        setPan({
                                            x: cW / 2 - midX * nz,
                                            y: cH / 2 - midY * nz
                                        });
                                    }}
                                    title="Fit all cards into view"
                                >
                                    ⊡ Fit
                                </button>
                            </div>

                            {/* Scroll Container */}
                            <div 
                                ref={adminBoardRef}
                                onMouseDown={handleCanvasMouseDown}
                                style={{
                                    position: "absolute",
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    overflow: "hidden",
                                    zIndex: 1,
                                    background: "transparent",
                                    cursor: canvasPanState.isPanning ? "grabbing" : (drawingMode ? "crosshair" : "grab")
                                }}
                            >
                                {/* 1600x1200 Workspace */}
                                <div style={{ 
                                    position: "absolute", 
                                    width: "1600px", 
                                    height: "1200px", 
                                    top: 0,
                                    left: 0,
                                    background: "transparent",
                                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoomScale})`,
                                    transformOrigin: "top left",
                                    transition: canvasPanState.isPanning ? "none" : "transform 0.15s ease-out",
                                    pointerEvents: "none"
                                }}>
                                    {/* Grid dots pattern layer */}
                                    <div style={{
                                        position: "absolute",
                                        top: 0, left: 0, right: 0, bottom: 0,
                                        backgroundImage: "radial-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 0)",
                                        backgroundSize: "20px 20px",
                                        zIndex: 0,
                                        pointerEvents: "none"
                                    }} />

                            {/* SVG Connection Lines Overlay */}
                            <svg style={{ position: "absolute", width: "100%", height: "100%", top: 0, left: 0, pointerEvents: "none", zIndex: 1 }}>
                                {loreNodes.map(node => {
                                    return (node.connections || []).map(connId => {
                                        const target = loreNodes.find(n => n.id === connId);
                                        if (!target || node.id > target.id) return null; // Avoid duplicate lines
                                        return (
                                            <line 
                                                key={`${node.id}-${target.id}`}
                                                x1={`${node.x}%`} 
                                                y1={`${node.y}%`} 
                                                x2={`${target.x}%`} 
                                                y2={`${target.y}%`} 
                                                stroke="rgba(255, 255, 255, 0.15)" 
                                                strokeWidth="2" 
                                                strokeDasharray="4 4"
                                            />
                                        );
                                    });
                                })}

                                {/* Custom Board Lines */}
                                {decorations.filter(d => d.type === "line").map(d => (
                                    <line 
                                        key={d.id}
                                        x1={`${d.x}%`} 
                                        y1={`${d.y}%`} 
                                        x2={`${d.x2 || (d.x + 10)}%`} 
                                        y2={`${d.y2 || d.y}%`} 
                                        stroke={d.color || "#fbbf24"}
                                        strokeWidth={d.strokeWidth || 3}
                                        strokeDasharray={d.dashed ? "4 4" : "none"}
                                        onClick={(e) => { e.stopPropagation(); setSelectedDecoration(d); setShowBgConfig(false); }}
                                        style={{ cursor: "pointer", pointerEvents: "auto" }}
                                    />
                                ))}
                            </svg>

                            {/* Relative viewBox SVG for Freehand Scribbles */}
                            <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: "absolute", width: "100%", height: "100%", top: 0, left: 0, zIndex: 1, pointerEvents: "none" }}>
                                {decorations.filter(d => d.type === "scribble").map(d => {
                                    if (!d.points || d.points.length < 2) return null;
                                    const pathData = d.points.map((p, idx) => `${idx === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
                                    return (
                                        <path
                                            key={d.id}
                                            d={pathData}
                                            fill="none"
                                            stroke={d.color || "#ff0055"}
                                            strokeWidth={d.strokeWidth ? d.strokeWidth * 0.1 : 0.3}
                                            onClick={(e) => { e.stopPropagation(); setSelectedDecoration(d); setShowBgConfig(false); }}
                                            style={{ cursor: "pointer", pointerEvents: "auto" }}
                                        />
                                    );
                                })}
                            </svg>

                            {/* Whiteboard Decorations Layer */}
                            {decorations.map(d => {
                                if (d.type === "title") {
                                    return (
                                        <div 
                                            key={d.id}
                                            onMouseDown={(e) => handleMouseDown(e, d.id, "decoration", d.x, d.y)}
                                            onClick={(e) => { e.stopPropagation(); setSelectedDecoration(d); setShowBgConfig(false); }}
                                            style={{
                                                position: "absolute",
                                                left: `${d.x}%`,
                                                top: `${d.y}%`,
                                                transform: "translate(-50%, -50%)",
                                                color: d.color || "#ffffff",
                                                fontSize: `${d.fontSize || 24}px`,
                                                fontWeight: 700,
                                                whiteSpace: "nowrap",
                                                cursor: "grab",
                                                zIndex: 3,
                                                userSelect: "none",
                                                textShadow: "1px 1px 4px rgba(0,0,0,0.8)",
                                                border: selectedDecoration?.id === d.id ? "1.5px dashed #38bdf8" : "none",
                                                padding: selectedDecoration?.id === d.id ? "2px 6px" : "0",
                                                borderRadius: 4
                                            }}
                                        >
                                            {d.text}
                                        </div>
                                    );
                                }
                                if (d.type === "shape") {
                                    return (
                                        <div 
                                            key={d.id}
                                            onMouseDown={(e) => handleMouseDown(e, d.id, "decoration", d.x, d.y)}
                                            onClick={(e) => { e.stopPropagation(); setSelectedDecoration(d); setShowBgConfig(false); }}
                                            style={{
                                                position: "absolute",
                                                left: `${d.x}%`,
                                                top: `${d.y}%`,
                                                transform: "translate(-50%, -50%)",
                                                width: `${d.width || 15}%`,
                                                height: `${d.height || 10}%`,
                                                border: `2px ${selectedDecoration?.id === d.id ? "dashed" : "solid"} ${d.color || "#38bdf8"}`,
                                                background: `${d.color || "#38bdf8"}22`,
                                                borderRadius: d.shapeType === "circle" ? "50%" : "8px",
                                                cursor: "grab",
                                                zIndex: 2
                                            }}
                                        />
                                    );
                                }
                                if (d.type === "image") {
                                    return (
                                        <div 
                                            key={d.id}
                                            onMouseDown={(e) => handleMouseDown(e, d.id, "decoration", d.x, d.y)}
                                            onClick={(e) => { e.stopPropagation(); setSelectedDecoration(d); setShowBgConfig(false); }}
                                            style={{
                                                position: "absolute",
                                                left: `${d.x}%`,
                                                top: `${d.y}%`,
                                                transform: "translate(-50%, -50%)",
                                                width: `${d.width || 20}%`,
                                                height: `${d.height || 15}%`,
                                                border: `2px ${selectedDecoration?.id === d.id ? "dashed #38bdf8" : "solid rgba(255,255,255,0.15)"}`,
                                                borderRadius: "8px",
                                                overflow: "hidden",
                                                cursor: "grab",
                                                zIndex: 2,
                                                background: "#0b0f19"
                                            }}
                                        >
                                            {d.imageUrl ? (
                                                <img 
                                                    src={d.imageUrl} 
                                                    alt="Decoration" 
                                                    style={{ 
                                                        width: "100%", 
                                                        height: "100%", 
                                                        objectFit: "cover", 
                                                        objectPosition: d.imagePosition || "center center" 
                                                    }} 
                                                />
                                            ) : (
                                                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", fontSize: "11px" }}>
                                                    No Image
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                return null;
                            })}

                            {/* Drag handles for selected Custom Lines */}
                            {decorations.filter(d => d.type === "line").map(d => {
                                if (selectedDecoration?.id !== d.id) return null;
                                return (
                                    <React.Fragment key={`handles-${d.id}`}>
                                        <div 
                                            onMouseDown={(e) => handleMouseDown(e, d.id, "decoration", d.x, d.y)}
                                            style={{
                                                position: "absolute",
                                                left: `${d.x}%`,
                                                top: `${d.y}%`,
                                                transform: "translate(-50%, -50%)",
                                                width: 12,
                                                height: 12,
                                                borderRadius: "50%",
                                                background: "#ffffff",
                                                border: "2px solid #06b6d4",
                                                cursor: "move",
                                                zIndex: 4,
                                                boxShadow: "0 0 5px rgba(0,0,0,0.5)"
                                            }}
                                        />
                                        <div 
                                            onMouseDown={(e) => handleMouseDown(e, d.id, "decorationEnd", d.x2 || (d.x + 10), d.y2 || d.y)}
                                            style={{
                                                position: "absolute",
                                                left: `${d.x2 || (d.x + 10)}%`,
                                                top: `${d.y2 || d.y}%`,
                                                transform: "translate(-50%, -50%)",
                                                width: 12,
                                                height: 12,
                                                borderRadius: "50%",
                                                background: "#ffffff",
                                                border: "2px solid #06b6d4",
                                                cursor: "move",
                                                zIndex: 4,
                                                boxShadow: "0 0 5px rgba(0,0,0,0.5)"
                                            }}
                                        />
                                    </React.Fragment>
                                );
                            })}

                            {/* Freehand Drawing Overlay Layer */}
                            {drawingMode && (
                                <div 
                                    onMouseDown={handleDrawingMouseDown}
                                    onMouseMove={handleDrawingMouseMove}
                                    onMouseUp={handleDrawingMouseUp}
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        zIndex: 8,
                                        cursor: "crosshair",
                                        background: "rgba(0,0,0,0.05)",
                                        pointerEvents: "auto"
                                    }}
                                >
                                    <div style={{
                                        position: "absolute",
                                        top: 12,
                                        left: 12,
                                        background: "rgba(15, 23, 42, 0.9)",
                                        border: "1px solid #334155",
                                        borderRadius: 8,
                                        padding: "8px 12px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 12,
                                        pointerEvents: "auto",
                                        zIndex: 10
                                    }} onMouseDown={e => e.stopPropagation()}>
                                        <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700 }}>SKETCH COLOR:</span>
                                        <input type="color" value={strokeColor} onChange={e => setStrokeColor(e.target.value)} style={{ width: 28, height: 28, padding: 0, border: "none", cursor: "pointer" }} />
                                        
                                        <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700 }}>WIDTH:</span>
                                        <input type="range" min="1" max="10" value={strokeWidth} onChange={e => setStrokeWidth(parseInt(e.target.value))} style={{ width: 60 }} />
                                        <span style={{ fontSize: 11, color: "white" }}>{strokeWidth}px</span>
                                    </div>
                                    
                                    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: "100%", height: "100%", pointerEvents: "none" }}>
                                        {currentScribblePoints.length > 1 && (
                                            <polyline
                                                points={currentScribblePoints.map(p => `${p.x},${p.y}`).join(" ")}
                                                fill="none"
                                                stroke={strokeColor}
                                                strokeWidth={strokeWidth * 0.1}
                                            />
                                        )}
                                    </svg>
                                </div>
                            )}

                            {/* Node Cards */}
                            {loreNodes.length === 0 ? (
                                <div style={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center", flexDirection: "column", color: "#64748b" }}>
                                    <FiLayout size={40} style={{ marginBottom: 12 }} />
                                    <p style={{ fontSize: 14, margin: 0 }}>This canvas is empty. Add a lore card to begin.</p>
                                </div>
                            ) : (
                                loreNodes.map(n => {
                                    const typeInfo = LORE_TYPES.find(t => t.id === n.type) || LORE_TYPES[0];
                                    const Icon = typeInfo.icon;
                                    return (
                                        <div 
                                            key={n.id}
                                            onMouseDown={(e) => handleMouseDown(e, n.id, "node", n.x, n.y)}
                                            onDoubleClick={() => startEditNode(n)}
                                            onClick={() => { setSelectedDecoration(null); setShowBgConfig(false); }}
                                            style={{
                                                position: "absolute",
                                                left: `${n.x}%`,
                                                top: `${n.y}%`,
                                                transform: "translate(-50%, -50%)",
                                                background: "rgba(15, 23, 42, 0.9)",
                                                backdropFilter: "blur(8px)",
                                                border: `2px solid ${typeInfo.color}`,
                                                color: "white",
                                                borderRadius: 10,
                                                cursor: "grab",
                                                zIndex: 5,
                                                userSelect: "none",
                                                width: "15%",
                                                height: "22%",
                                                boxShadow: `0 4px 15px rgba(0,0,0,0.3), 0 0 10px ${typeInfo.color}33`,
                                                display: "flex",
                                                flexDirection: "column",
                                                overflow: "hidden"
                                            }}
                                        >
                                            {/* Card image/preview top portion */}
                                            <div style={{ position: "relative", width: "100%", flex: 1, background: "#0b0f19", overflow: "hidden", minHeight: 0 }}>
                                                {n.imageUrl ? (
                                                    <img 
                                                        src={n.imageUrl} 
                                                        alt={n.title} 
                                                        style={{ 
                                                            width: "100%", 
                                                            height: "100%", 
                                                            objectFit: "cover", 
                                                            objectPosition: n.imagePosition || "center center" 
                                                        }}
                                                    />
                                                ) : (
                                                    <div style={{ 
                                                        width: "100%", 
                                                        height: "100%", 
                                                        background: `linear-gradient(135deg, #1e293b 0%, ${typeInfo.color}22 100%)`, 
                                                        display: "flex", 
                                                        alignItems: "center", 
                                                        justifyContent: "center" 
                                                    }}>
                                                        <Icon size={24} style={{ color: `${typeInfo.color}66` }} />
                                                    </div>
                                                )}
                                            </div>
                                            {/* Card title bottom portion */}
                                            <div style={{ 
                                                background: "rgba(15, 23, 42, 0.95)", 
                                                padding: "6px 8px", 
                                                flexShrink: 0,
                                                borderTop: "1px solid rgba(255,255,255,0.05)"
                                            }}>
                                                <p style={{ 
                                                    margin: 0, 
                                                    fontSize: "11px", 
                                                    fontWeight: 700, 
                                                    overflow: "hidden", 
                                                    textOverflow: "ellipsis", 
                                                    whiteSpace: "nowrap",
                                                    color: "white"
                                                }}>
                                                    {n.title}
                                                </p>
                                                <p style={{ margin: 0, fontSize: "8px", color: typeInfo.color, fontWeight: 600, textTransform: "uppercase" }}>
                                                    {n.type === "character" ? (n.characterRole || "Character") : typeInfo.label}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                            </div>
                    </div> {/* adminBoardRef closing */}
                </div> {/* adminCanvasContainerRef closing */}
            </div> {/* visual editor panel wrapper closing */}

                    {/* Nodes & Configuration Sidebar list */}
                    {showBgConfig ? (
                        <div className="admin-card" style={{ padding: 18, maxHeight: "630px", overflowY: "auto" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                                <p style={{ fontWeight: 700, fontSize: 13, textTransform: "uppercase", color: "#888", margin: 0 }}>Canvas Background</p>
                                <button className="admin-btn-secondary" style={{ padding: "4px 8px", fontSize: 10 }} onClick={() => setShowBgConfig(false)}><FiX size={12} /></button>
                            </div>
                            <div className="admin-form" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                <div className="admin-form-field">
                                    <label>Background Image URL</label>
                                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                        <input value={bgForm.bgImageUrl} onChange={e => setBgForm({...bgForm, bgImageUrl: e.target.value})} placeholder="Image URL..." style={{ flex: 1, fontSize: 12 }} />
                                        <label className="admin-btn-secondary" style={{ padding: "8px 12px", cursor: "pointer", display: "flex", alignItems: "center", margin: 0 }}>
                                            <FiUpload size={12} />
                                            <input type="file" accept="image/*" onChange={e => uploadImage(e, url => setBgForm({...bgForm, bgImageUrl: url}))} style={{ display: "none" }} />
                                        </label>
                                    </div>
                                </div>
                                <div className="admin-form-field">
                                    <label>Background Alignment</label>
                                    <select value={bgForm.bgImagePosition} onChange={e => setBgForm({...bgForm, bgImagePosition: e.target.value})} style={{ fontSize: 12 }}>
                                        <option value="center center">Center</option>
                                        <option value="top center">Top</option>
                                        <option value="bottom center">Bottom</option>
                                        <option value="center left">Left</option>
                                        <option value="center right">Right</option>
                                    </select>
                                </div>
                                <div className="admin-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                    <div className="admin-form-field">
                                        <label>Overlay Color</label>
                                        <input type="color" value={bgForm.bgOverlayColor} onChange={e => setBgForm({...bgForm, bgOverlayColor: e.target.value})} style={{ width: "100%", height: 32, padding: 0, border: "none" }} />
                                    </div>
                                    <div className="admin-form-field">
                                        <label>Overlay Opacity</label>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            <input type="range" min="0" max="1" step="0.05" value={bgForm.bgOverlayOpacity} onChange={e => setBgForm({...bgForm, bgOverlayOpacity: parseFloat(e.target.value)})} style={{ flex: 1 }} />
                                            <span style={{ fontSize: 11, fontWeight: 700 }}>{Math.round(bgForm.bgOverlayOpacity * 100)}%</span>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                                    <button className="admin-btn-secondary" style={{ flex: 1, fontSize: 12 }} onClick={() => setShowBgConfig(false)}>Cancel</button>
                                    <button className="admin-btn-primary" style={{ flex: 1, fontSize: 12 }} onClick={saveBgConfig}>Save Config</button>
                                </div>
                            </div>
                        </div>
                    ) : selectedDecoration ? (
                        <div className="admin-card" style={{ padding: 18, maxHeight: "630px", overflowY: "auto" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                                <p style={{ fontWeight: 700, fontSize: 13, textTransform: "uppercase", color: "#888", margin: 0 }}>Edit Annotation</p>
                                <button className="admin-btn-secondary" style={{ padding: "4px 8px", fontSize: 10 }} onClick={() => setSelectedDecoration(null)}><FiX size={12} /></button>
                            </div>
                            <div className="admin-form" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                {selectedDecoration.type === "title" && (
                                    <>
                                        <div className="admin-form-field">
                                            <label>Label Text</label>
                                            <input value={selectedDecoration.text || ""} onChange={e => setSelectedDecoration({...selectedDecoration, text: e.target.value})} placeholder="Label..." style={{ fontSize: 12 }} />
                                        </div>
                                        <div className="admin-form-field">
                                            <label>Font Size (px)</label>
                                            <input type="number" min="10" max="72" value={selectedDecoration.fontSize || 24} onChange={e => setSelectedDecoration({...selectedDecoration, fontSize: parseInt(e.target.value) || 24})} style={{ fontSize: 12 }} />
                                        </div>
                                    </>
                                )}
                                {selectedDecoration.type === "shape" && (
                                    <>
                                        <div className="admin-form-field">
                                            <label>Shape Type</label>
                                            <select value={selectedDecoration.shapeType || "rectangle"} onChange={e => setSelectedDecoration({...selectedDecoration, shapeType: e.target.value})} style={{ fontSize: 12 }}>
                                                <option value="rectangle">Rectangle</option>
                                                <option value="circle">Circle</option>
                                            </select>
                                        </div>
                                        <div className="admin-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                            <div className="admin-form-field">
                                                <label>Width (%)</label>
                                                <input type="number" min="1" max="100" value={selectedDecoration.width || 10} onChange={e => setSelectedDecoration({...selectedDecoration, width: parseInt(e.target.value) || 10})} style={{ fontSize: 12 }} />
                                            </div>
                                            <div className="admin-form-field">
                                                <label>Height (%)</label>
                                                <input type="number" min="1" max="100" value={selectedDecoration.height || 10} onChange={e => setSelectedDecoration({...selectedDecoration, height: parseInt(e.target.value) || 10})} style={{ fontSize: 12 }} />
                                            </div>
                                        </div>
                                    </>
                                )}
                                {selectedDecoration.type === "line" && (
                                    <>
                                        <div className="admin-form-field">
                                            <label>Stroke Width (px)</label>
                                            <input type="number" min="1" max="20" value={selectedDecoration.strokeWidth || 3} onChange={e => setSelectedDecoration({...selectedDecoration, strokeWidth: parseInt(e.target.value) || 3})} style={{ fontSize: 12 }} />
                                        </div>
                                        <div className="admin-form-field">
                                            <label>Line Style</label>
                                            <select value={selectedDecoration.dashed ? "dashed" : "solid"} onChange={e => setSelectedDecoration({...selectedDecoration, dashed: e.target.value === "dashed"})} style={{ fontSize: 12 }}>
                                                <option value="solid">Solid Line</option>
                                                <option value="dashed">Dashed Line</option>
                                            </select>
                                        </div>
                                    </>
                                )}
                                {selectedDecoration.type === "image" && (
                                    <>
                                        <div className="admin-form-field">
                                            <label>Image URL</label>
                                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                <input value={selectedDecoration.imageUrl || ""} onChange={e => setSelectedDecoration({...selectedDecoration, imageUrl: e.target.value})} placeholder="Image URL..." style={{ flex: 1, fontSize: 12 }} />
                                                <label className="admin-btn-secondary" style={{ padding: "8px 12px", cursor: "pointer", display: "flex", alignItems: "center", margin: 0 }}>
                                                    <FiUpload size={12} />
                                                    <input type="file" accept="image/*" onChange={e => uploadImage(e, url => setSelectedDecoration({...selectedDecoration, imageUrl: url}))} style={{ display: "none" }} />
                                                </label>
                                            </div>
                                        </div>
                                        <div className="admin-form-field">
                                            <label>Crop / Focus Position</label>
                                            <select value={selectedDecoration.imagePosition || "center center"} onChange={e => setSelectedDecoration({...selectedDecoration, imagePosition: e.target.value})} style={{ fontSize: 12 }}>
                                                <option value="center center">Center</option>
                                                <option value="top center">Top</option>
                                                <option value="bottom center">Bottom</option>
                                                <option value="center left">Left</option>
                                                <option value="center right">Right</option>
                                            </select>
                                        </div>
                                        <div className="admin-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                            <div className="admin-form-field">
                                                <label>Width (%)</label>
                                                <input type="number" min="1" max="100" value={selectedDecoration.width || 20} onChange={e => setSelectedDecoration({...selectedDecoration, width: parseInt(e.target.value) || 20})} style={{ fontSize: 12 }} />
                                            </div>
                                            <div className="admin-form-field">
                                                <label>Height (%)</label>
                                                <input type="number" min="1" max="100" value={selectedDecoration.height || 15} onChange={e => setSelectedDecoration({...selectedDecoration, height: parseInt(e.target.value) || 15})} style={{ fontSize: 12 }} />
                                            </div>
                                        </div>
                                    </>
                                )}
                                {selectedDecoration.type === "scribble" && (
                                    <div className="admin-form-field">
                                        <label>Stroke Width (px)</label>
                                        <input type="number" min="1" max="20" value={selectedDecoration.strokeWidth || 3} onChange={e => setSelectedDecoration({...selectedDecoration, strokeWidth: parseInt(e.target.value) || 3})} style={{ fontSize: 12 }} />
                                    </div>
                                )}
                                {selectedDecoration.type !== "image" && (
                                    <div className="admin-form-field">
                                        <label>Color</label>
                                        <input type="color" value={selectedDecoration.color || (selectedDecoration.type === "scribble" ? "#ff0055" : "#ffffff")} onChange={e => setSelectedDecoration({...selectedDecoration, color: e.target.value})} style={{ width: "100%", height: 32, padding: 0, border: "none" }} />
                                    </div>
                                )}
                                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                                    <div style={{ display: "flex", gap: 8 }}>
                                        <button className="admin-btn-secondary" style={{ flex: 1, fontSize: 12 }} onClick={() => setSelectedDecoration(null)}>Cancel</button>
                                        <button className="admin-btn-primary" style={{ flex: 1, fontSize: 12 }} onClick={saveDecoration}>Save Changes</button>
                                    </div>
                                    <button className="admin-btn-danger" style={{ width: "100%", fontSize: 12, marginTop: 4 }} onClick={() => deleteDecoration(selectedDecoration.id)}>Delete Annotation</button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="admin-card" style={{ padding: 18, maxHeight: "630px", overflowY: "auto" }}>
                            {/* Tab Selectors */}
                            <div style={{ display: "flex", gap: 10, borderBottom: "1px solid #334155", paddingBottom: 10, marginBottom: 14 }}>
                                <button 
                                    className={`admin-btn-secondary ${sidebarTab === "cards" ? "active" : ""}`}
                                    onClick={() => setSidebarTab("cards")}
                                    style={{ 
                                        flex: 1, 
                                        fontSize: 11, 
                                        fontWeight: 700, 
                                        padding: "6px 8px",
                                        background: sidebarTab === "cards" ? "#38bdf8" : "",
                                        borderColor: sidebarTab === "cards" ? "#0284c7" : "",
                                        color: sidebarTab === "cards" ? "#0f172a" : "#94a3b8"
                                    }}
                                >
                                    Lore Cards ({loreNodes.length})
                                </button>
                                <button 
                                    className={`admin-btn-secondary ${sidebarTab === "annotations" ? "active" : ""}`}
                                    onClick={() => setSidebarTab("annotations")}
                                    style={{ 
                                        flex: 1, 
                                        fontSize: 11, 
                                        fontWeight: 700, 
                                        padding: "6px 8px",
                                        background: sidebarTab === "annotations" ? "#fbbf24" : "",
                                        borderColor: sidebarTab === "annotations" ? "#d97706" : "",
                                        color: sidebarTab === "annotations" ? "#0f172a" : "#94a3b8"
                                    }}
                                >
                                    Annotations ({decorations.length})
                                </button>
                            </div>

                            {sidebarTab === "cards" ? (
                                loreNodes.length === 0 ? (
                                    <p style={{ fontSize: 12, color: "#aaa", textAlign: "center", margin: "20px 0" }}>No lore cards created.</p>
                                ) : (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                        {loreNodes.map(n => {
                                            const typeInfo = LORE_TYPES.find(t => t.id === n.type);
                                            return (
                                                <div key={n.id} style={{ 
                                                    display: "flex", 
                                                    justifyContent: "space-between", 
                                                    alignItems: "center", 
                                                    padding: "10px 12px", 
                                                    background: "#1e293b", 
                                                    borderRadius: 8, 
                                                    borderLeft: `4px solid ${typeInfo?.color || "#ddd"}`,
                                                    border: "1px solid #334155"
                                                }}>
                                                    <div style={{ minWidth: 0, marginRight: 8 }}>
                                                        <span style={{ fontSize: 12, fontWeight: 700, display: "block", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", color: "#f1f5f9" }}>{n.title}</span>
                                                        <span style={{ fontSize: 10, color: "#94a3b8" }}>Connections: {n.connections?.length || 0}</span>
                                                    </div>
                                                    <div style={{ display: "flex", gap: 4 }}>
                                                        <button onClick={() => startEditNode(n)} className="admin-btn-secondary" style={{ padding: "4px 8px", fontSize: 10 }}><FiEdit2 size={11} /></button>
                                                        <button onClick={() => deleteNode(n.id)} className="admin-btn-danger" style={{ padding: "4px 8px", fontSize: 10 }}><FiTrash2 size={11} /></button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )
                            ) : (
                                decorations.length === 0 ? (
                                    <p style={{ fontSize: 12, color: "#aaa", textAlign: "center", margin: "20px 0" }}>No annotations created.</p>
                                ) : (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                        {decorations.map(d => {
                                            let label = "";
                                            if (d.type === "title") label = `Label: "${d.text}"`;
                                            else if (d.type === "shape") label = `${d.shapeType === "circle" ? "Circle" : "Rectangle"} Shape`;
                                            else if (d.type === "line") label = `${d.dashed ? "Dashed" : "Solid"} Line`;
                                            else if (d.type === "image") label = `Image: ${d.imageUrl ? d.imageUrl.split('/').pop() : "No Image"}`;
                                            else if (d.type === "scribble") label = "Freehand Sketch";
                                            else label = d.type;

                                            return (
                                                <div key={d.id} style={{ 
                                                    display: "flex", 
                                                    justifyContent: "space-between", 
                                                    alignItems: "center", 
                                                    padding: "10px 12px", 
                                                    background: "#1e293b", 
                                                    borderRadius: 8, 
                                                    borderLeft: "4px solid #fbbf24",
                                                    border: "1px solid #334155" 
                                                }}>
                                                    <div style={{ minWidth: 0, marginRight: 8 }}>
                                                        <span style={{ fontSize: 12, fontWeight: 700, display: "block", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", color: "#f1f5f9" }}>{label}</span>
                                                        <span style={{ fontSize: 10, color: "#94a3b8" }}>Position: {d.x}% / {d.y}%</span>
                                                    </div>
                                                    <div style={{ display: "flex", gap: 4 }}>
                                                        <button onClick={() => { setSelectedDecoration(d); setShowBgConfig(false); }} className="admin-btn-secondary" style={{ padding: "4px 8px", fontSize: 10 }}><FiEdit2 size={11} /></button>
                                                        <button onClick={() => deleteDecoration(d.id)} className="admin-btn-danger" style={{ padding: "4px 8px", fontSize: 10 }}><FiTrash2 size={11} /></button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>

                {/* Lore Node Edit Modal */}
                {showNodeModal && (
                    <div className="admin-modal-backdrop" onClick={e => e.target === e.currentTarget && setShowNodeModal(false)}>
                        <div className="admin-modal" style={{ maxWidth: 700 }}>
                            <div className="admin-modal-header">
                                <h3>{editingNodeId ? "Edit Lore Card" : "New Lore Card"}</h3>
                                <button className="admin-modal-close" onClick={() => setShowNodeModal(false)}><FiX size={20} /></button>
                            </div>
                            <div className="admin-modal-body">
                                <div className="admin-form">
                                    <div className="admin-form-field">
                                        <label>Card Title / Entity Name *</label>
                                        <input value={nodeForm.title} onChange={e => setNodeForm({...nodeForm, title: e.target.value})} placeholder="e.g. Valen the Archmage" />
                                    </div>
                                    
                                    <div className="admin-form-field">
                                        <label>Entity Category / Type</label>
                                        <select value={nodeForm.type} onChange={e => setNodeForm({...nodeForm, type: e.target.value})}>
                                            {LORE_TYPES.map(t => (
                                                <option key={t.id} value={t.id}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="admin-form-field" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                                        <input 
                                            type="checkbox" 
                                            id="isSpoilerCheckbox"
                                            checked={nodeForm.isSpoiler || false} 
                                            onChange={e => setNodeForm({...nodeForm, isSpoiler: e.target.checked})} 
                                            style={{ width: 16, height: 16, cursor: "pointer" }}
                                        />
                                        <label htmlFor="isSpoilerCheckbox" style={{ margin: 0, cursor: "pointer", fontWeight: 700, color: "#ef4444" }}>
                                            ⚠️ Mark this entire card as a spoiler item (hidden by default on canvas)
                                        </label>
                                    </div>

                                    {nodeForm.type === "character" && (
                                        <>
                                            <div className="admin-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                                                <div className="admin-form-field">
                                                    <label>Age</label>
                                                    <input value={nodeForm.age} onChange={e => setNodeForm({...nodeForm, age: e.target.value})} placeholder="e.g. 24, Unknown, Eternal" />
                                                </div>
                                                <div className="admin-form-field">
                                                    <label>Role</label>
                                                    <select value={nodeForm.characterRole} onChange={e => setNodeForm({...nodeForm, characterRole: e.target.value})}>
                                                        <option value="Protagonist">Protagonist</option>
                                                        <option value="Secondary Protagonist">Secondary Protagonist</option>
                                                        <option value="Antagonist">Antagonist</option>
                                                        <option value="Supporting Character">Supporting Character</option>
                                                        <option value="Sidekick">Sidekick</option>
                                                        <option value="Mentor">Mentor</option>
                                                        <option value="Villain">Villain</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </div>
                                                <div className="admin-form-field">
                                                    <label>Class</label>
                                                    <select value={nodeForm.characterClass} onChange={e => setNodeForm({...nodeForm, characterClass: e.target.value})}>
                                                        <option value="Vampire">Vampire</option>
                                                        <option value="Fae">Fae</option>
                                                        <option value="Werewolf">Werewolf</option>
                                                        <option value="Witch">Witch</option>
                                                        <option value="Hybrid">Hybrid</option>
                                                        <option value="Human">Human</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="admin-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                                <div className="admin-form-field">
                                                    <label>Physical Appearance</label>
                                                    <input value={nodeForm.physical} onChange={e => setNodeForm({...nodeForm, physical: e.target.value})} placeholder="e.g. Tall, silver hair, glowing eyes" />
                                                </div>
                                                <div className="admin-form-field">
                                                    <label>Personality Traits</label>
                                                    <input value={nodeForm.personality} onChange={e => setNodeForm({...nodeForm, personality: e.target.value})} placeholder="e.g. Calm, ambitious, loyal" />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    <div className="admin-form-field">
                                        <label>Card Image / Avatar (Optional)</label>
                                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                                            <input value={nodeForm.imageUrl} onChange={e => setNodeForm({...nodeForm, imageUrl: e.target.value})} placeholder="Image URL..." style={{ flex: 1 }} />
                                            <label className="admin-btn-secondary" style={{ padding: "10px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
                                                <FiUpload size={14} /> {uploading ? "Uploading..." : "Upload File"}
                                                <input type="file" accept="image/*" onChange={e => uploadImage(e, url => setNodeForm({...nodeForm, imageUrl: url}))} style={{ display: "none" }} />
                                            </label>
                                        </div>
                                        {nodeForm.imageUrl && (
                                            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 12 }}>
                                                <img 
                                                    src={nodeForm.imageUrl} 
                                                    alt="Preview" 
                                                    style={{ 
                                                        width: 60, 
                                                        height: 60, 
                                                        objectFit: "cover", 
                                                        borderRadius: 8, 
                                                        border: "1.5px solid #e2e8f0",
                                                        boxShadow: "0 4px 6px rgba(0,0,0,0.05)" 
                                                    }} 
                                                />
                                                <button 
                                                    type="button" 
                                                    className="admin-btn-danger" 
                                                    onClick={() => setNodeForm({...nodeForm, imageUrl: ""})} 
                                                    style={{ padding: "4px 8px", fontSize: 11 }}
                                                >
                                                    Remove Image
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {nodeForm.imageUrl && (
                                        <div className="admin-form-field">
                                            <label>Image Focus Alignment</label>
                                            <select value={nodeForm.imagePosition} onChange={e => setNodeForm({...nodeForm, imagePosition: e.target.value})}>
                                                <option value="center center">Center (Default)</option>
                                                <option value="top center">Top Center (Face focus for vertical portraits)</option>
                                                <option value="bottom center">Bottom Center</option>
                                                <option value="center left">Center Left</option>
                                                <option value="center right">Center Right</option>
                                            </select>
                                        </div>
                                    )}

                                    {/* Relationship Connections */}
                                    <div className="admin-form-field">
                                        <label>Connect to other lore cards in this Saga</label>
                                        <div style={{ 
                                            border: "1.5px solid #eee", 
                                            borderRadius: 8, 
                                            padding: 12, 
                                            maxHeight: 120, 
                                            overflowY: "auto",
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr",
                                            gap: 8
                                        }}>
                                            {loreNodes.filter(n => n.id !== editingNodeId).length === 0 ? (
                                                <p style={{ gridColumn: "1 / -1", margin: 0, fontSize: 11, color: "#aaa", textAlign: "center" }}>No other lore cards available to connect to.</p>
                                            ) : (
                                                loreNodes.filter(n => n.id !== editingNodeId).map(n => {
                                                    const checked = nodeForm.connections.includes(n.id);
                                                    return (
                                                        <label key={n.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer", margin: 0 }}>
                                                            <input type="checkbox" checked={checked} onChange={() => toggleConnection(n.id)} style={{ width: 14, height: 14 }} />
                                                            {n.title} ({n.type})
                                                        </label>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>

                                    <div className="admin-form-field full" style={{ minHeight: "260px" }}>
                                        <div style={{ display: "flex", gap: 12, marginBottom: 14, borderBottom: "1.5px solid #cbd5e1", paddingBottom: 6 }}>
                                            <button 
                                                type="button"
                                                onClick={() => setActiveEditorTab("standard")}
                                                style={{ 
                                                    background: "none", 
                                                    border: "none", 
                                                    borderBottom: activeEditorTab === "standard" ? "3px solid #3b82f6" : "3px solid transparent",
                                                    fontWeight: 700,
                                                    fontSize: "13px",
                                                    color: activeEditorTab === "standard" ? "#1d4ed8" : "#64748b",
                                                    padding: "6px 12px",
                                                    cursor: "pointer",
                                                    transition: "all 0.15s"
                                                }}
                                            >
                                                Preview
                                            </button>
                                            <button 
                                                type="button"
                                                onClick={() => setActiveEditorTab("book1")}
                                                style={{ 
                                                    background: "none", 
                                                    border: "none", 
                                                    borderBottom: activeEditorTab === "book1" ? "3px solid #ef4444" : "3px solid transparent",
                                                    fontWeight: 700,
                                                    fontSize: "13px",
                                                    color: activeEditorTab === "book1" ? "#b91c1c" : "#64748b",
                                                    padding: "6px 12px",
                                                    cursor: "pointer",
                                                    transition: "all 0.15s"
                                                }}
                                            >
                                                Did you read book 1? (Spoiler)
                                            </button>
                                            <button 
                                                type="button"
                                                onClick={() => setActiveEditorTab("book2")}
                                                style={{ 
                                                    background: "none", 
                                                    border: "none", 
                                                    borderBottom: activeEditorTab === "book2" ? "3px solid #dc2626" : "3px solid transparent",
                                                    fontWeight: 700,
                                                    fontSize: "13px",
                                                    color: activeEditorTab === "book2" ? "#991b1b" : "#64748b",
                                                    padding: "6px 12px",
                                                    cursor: "pointer",
                                                    transition: "all 0.15s"
                                                }}
                                            >
                                                Did you read book 2(Spoiler)
                                            </button>
                                        </div>

                                        {activeEditorTab === "standard" && (
                                            <div>
                                                <label style={{ display: "block", marginBottom: 6, fontWeight: 700, fontSize: "11px", color: "#64748b", textTransform: "uppercase" }}>Preview Description</label>
                                                <ReactQuill 
                                                    value={nodeForm.content || ""} 
                                                    onChange={val => setNodeForm({...nodeForm, content: val})} 
                                                    modules={quillModules}
                                                    style={{ height: "140px", marginBottom: "12px" }}
                                                />
                                                <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: "12px", cursor: "pointer", color: nodeForm.contentIsSpoiler ? "#ef4444" : "#64748b", fontWeight: 600, marginTop: 44, userSelect: "none" }}>
                                                    <input type="checkbox" checked={nodeForm.contentIsSpoiler || false} onChange={e => setNodeForm({...nodeForm, contentIsSpoiler: e.target.checked})} style={{ width: 14, height: 14, cursor: "pointer", accentColor: "#ef4444" }} />
                                                    🔒 Hide this description as spoiler (readers need "Show Spoilers" ON to see it)
                                                </label>
                                            </div>
                                        )}
                                        {activeEditorTab === "book1" && (
                                            <div>
                                                <label style={{ display: "block", marginBottom: 6, fontWeight: 700, fontSize: "11px", color: "#ef4444", textTransform: "uppercase" }}>Did you read book 1? (Spoiler) Details</label>
                                                <ReactQuill 
                                                    value={nodeForm.book1Content || ""} 
                                                    onChange={val => setNodeForm({...nodeForm, book1Content: val})} 
                                                    modules={quillModules}
                                                    style={{ height: "140px", marginBottom: "12px" }}
                                                />
                                                <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: "12px", cursor: "pointer", color: nodeForm.book1IsSpoiler ? "#ef4444" : "#64748b", fontWeight: 600, marginTop: 44, userSelect: "none" }}>
                                                    <input type="checkbox" checked={nodeForm.book1IsSpoiler || false} onChange={e => setNodeForm({...nodeForm, book1IsSpoiler: e.target.checked})} style={{ width: 14, height: 14, cursor: "pointer", accentColor: "#ef4444" }} />
                                                    🔒 Hide this tab as spoiler (tab button hidden until "Show Spoilers" is ON)
                                                </label>
                                            </div>
                                        )}
                                        {activeEditorTab === "book2" && (
                                            <div>
                                                <label style={{ display: "block", marginBottom: 6, fontWeight: 700, fontSize: "11px", color: "#dc2626", textTransform: "uppercase" }}>Did you read book 2(Spoiler) Details</label>
                                                <ReactQuill 
                                                    value={nodeForm.book2Content || ""} 
                                                    onChange={val => setNodeForm({...nodeForm, book2Content: val})} 
                                                    modules={quillModules}
                                                    style={{ height: "140px", marginBottom: "12px" }}
                                                />
                                                <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: "12px", cursor: "pointer", color: nodeForm.book2IsSpoiler ? "#dc2626" : "#64748b", fontWeight: 600, marginTop: 44, userSelect: "none" }}>
                                                    <input type="checkbox" checked={nodeForm.book2IsSpoiler || false} onChange={e => setNodeForm({...nodeForm, book2IsSpoiler: e.target.checked})} style={{ width: 14, height: 14, cursor: "pointer", accentColor: "#dc2626" }} />
                                                    🔒 Hide this tab as spoiler (tab button hidden until "Show Spoilers" is ON)
                                                </label>
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: "flex", justifySelf: "end", gap: 8, marginTop: 12 }}>
                                        <button className="admin-btn-secondary" onClick={() => setShowNodeModal(false)}>Cancel</button>
                                        <button className="admin-btn-primary" onClick={saveNode}>Save Lore Card</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Default: Sagas Management list
    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                <h3 style={{ margin: 0 }}>Book Sagas (Wiki Canvas)</h3>
                <button className="admin-btn-primary" onClick={startAddSaga} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <FiPlus size={16} /> Add New Saga
                </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 24, alignItems: "start" }}>
                {/* Sagas table list */}
                <div className="admin-card">
                    {loadingSagas ? (
                        <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Loading sagas…</div>
                    ) : sagas.length === 0 ? (
                        <div className="admin-empty" style={{ padding: 40 }}><p>No Sagas created yet.</p></div>
                    ) : (
                        <div className="admin-table-wrap">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Book Render</th>
                                        <th>Saga Title</th>
                                        <th>Style</th>
                                        <th>Coords (X / Y)</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sagas.map(s => (
                                        <tr key={s.id}>
                                            <td>
                                                <div style={{ 
                                                    width: 32, 
                                                    height: 44, 
                                                    background: s.bookColor || "#8b1e0f", 
                                                    borderRadius: "2px 6px 6px 2px",
                                                    boxShadow: "2px 2px 5px rgba(0,0,0,0.2)",
                                                    borderLeft: "4px solid rgba(0,0,0,0.25)"
                                                }} />
                                            </td>
                                            <td>
                                                <strong>{s.title}</strong>
                                                <p style={{ margin: "2px 0 0", fontSize: 11, color: "#888", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{s.description}</p>
                                            </td>
                                            <td>
                                                <span style={{ fontSize: 11, background: "#f4f5f7", padding: "3px 8px", borderRadius: 4, textTransform: "capitalize" }}>
                                                    {s.bookStyle}
                                                </span>
                                            </td>
                                            <td style={{ fontSize: 12, color: "#666" }}>
                                                {s.x}% / {s.y}%
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", gap: 6 }}>
                                                    <button onClick={() => setSelectedSaga(s)} className="admin-btn-secondary" style={{ padding: "6px 12px", background: "#e0f2fe", borderColor: "#bae6fd", color: "#0369a1", display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                                                        Lore <FiChevronRight size={14} />
                                                    </button>
                                                    <button onClick={() => startEditSaga(s)} className="admin-btn-secondary" style={{ padding: "6px 10px" }}><FiEdit2 size={13} /></button>
                                                    <button onClick={() => deleteSaga(s.id)} className="admin-btn-danger" style={{ padding: "6px 10px" }}><FiTrash2 size={13} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Sagas constellation tabletop placement preview */}
                <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                        <p style={{ margin: 0, fontSize: 13, color: "#888", fontWeight: 700, textTransform: "uppercase" }}>Tabletop Coords</p>
                        <span style={{ fontSize: 9, background: "#dcfce7", color: "#166534", padding: "2px 6px", borderRadius: 4, fontWeight: 700 }}>VISUAL PLACEMENT</span>
                    </div>
                    <div style={{ 
                        position: "relative", 
                        width: "100%", 
                        height: "360px", 
                        background: "#181410", 
                        backgroundImage: "radial-gradient(#2d241d 1.5px, transparent 0)",
                        backgroundSize: "24px 24px",
                        border: "2px dashed #403228",
                        borderRadius: 12, 
                        overflow: "hidden"
                    }}>
                        {sagas.map(s => (
                            <div 
                                key={s.id}
                                onMouseDown={(e) => handleMouseDown(e, s.id, "saga", s.x, s.y)}
                                title={`${s.title} (Drag to position)`}
                                style={{
                                    position: "absolute",
                                    left: `${s.x}%`,
                                    top: `${s.y}%`,
                                    transform: "translate(-50%, -50%)",
                                    width: "28px",
                                    height: "38px",
                                    background: s.bookColor || "#8b1e0f",
                                    borderRadius: "2px 6px 6px 2px",
                                    cursor: "grab",
                                    zIndex: 10,
                                    borderLeft: "4px solid rgba(0,0,0,0.3)",
                                    boxShadow: "0 4px 10px rgba(0,0,0,0.5)",
                                    border: s.bookStyle === "golden" ? "1px solid #d4af37" : (s.bookStyle === "gothic" ? "1px solid #aaa" : "none")
                                }}
                            />
                        ))}
                    </div>
                    <p style={{ fontSize: 11, color: "#aaa", marginTop: 8, textAlign: "center" }}>Drag the book cards in this grid to position them on the visitor's Saga table.</p>
                </div>
            </div>

            {/* Saga Edit Modal */}
            {showSagaModal && (
                <div className="admin-modal-backdrop" onClick={e => e.target === e.currentTarget && setShowSagaModal(false)}>
                    <div className="admin-modal" style={{ maxWidth: 600 }}>
                        <div className="admin-modal-header">
                            <h3>{editingSagaId ? "Edit Saga Details" : "Create Book Saga"}</h3>
                            <button className="admin-modal-close" onClick={() => setShowSagaModal(false)}><FiX size={20} /></button>
                        </div>
                        <div className="admin-modal-body">
                            <div className="admin-form">
                                <div className="admin-form-field">
                                    <label>Saga Title *</label>
                                    <input value={sagaForm.title} onChange={e => setSagaForm({...sagaForm, title: e.target.value})} placeholder="e.g. The Chrono Chronicles" />
                                </div>
                                <div className="admin-form-field">
                                    <label>Saga Description Summary</label>
                                    <textarea value={sagaForm.description} onChange={e => setSagaForm({...sagaForm, description: e.target.value})} placeholder="Write a brief overview of the saga universe..." rows={3} />
                                </div>
                                <div className="admin-form-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
                                    <div className="admin-form-field">
                                        <label>Book Spine Color</label>
                                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                                            <input type="color" value={sagaForm.bookColor} onChange={e => setSagaForm({...sagaForm, bookColor: e.target.value})} style={{ width: 44, height: 40, padding: 0, border: "none", cursor: "pointer" }} />
                                            <span style={{ fontSize: 13, textTransform: "uppercase" }}>{sagaForm.bookColor}</span>
                                        </div>
                                    </div>
                                    <div className="admin-form-field">
                                        <label>Saga Book Bind Style</label>
                                        <select value={sagaForm.bookStyle} onChange={e => setSagaForm({...sagaForm, bookStyle: e.target.value})}>
                                            {BOOK_STYLES.map(s => (
                                                <option key={s.id} value={s.id}>{s.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="admin-form-field">
                                    <label>Custom Book Cover Image (Optional)</label>
                                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                                        <input value={sagaForm.imageUrl} onChange={e => setSagaForm({...sagaForm, imageUrl: e.target.value})} placeholder="Image URL..." style={{ flex: 1 }} />
                                        <label className="admin-btn-secondary" style={{ padding: "10px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
                                            <FiUpload size={14} /> {uploading ? "Uploading..." : "Upload File"}
                                            <input type="file" accept="image/*" onChange={e => uploadImage(e, url => setSagaForm({...sagaForm, imageUrl: url}))} style={{ display: "none" }} />
                                        </label>
                                    </div>
                                    {sagaForm.imageUrl && (
                                        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 12 }}>
                                            <img 
                                                src={sagaForm.imageUrl} 
                                                alt="Cover Preview" 
                                                style={{ 
                                                    width: 50, 
                                                    height: 70, 
                                                    objectFit: "cover", 
                                                    borderRadius: 4, 
                                                    border: "1.5px solid #e2e8f0",
                                                    boxShadow: "0 4px 6px rgba(0,0,0,0.05)" 
                                                }} 
                                            />
                                            <button 
                                                type="button" 
                                                className="admin-btn-danger" 
                                                onClick={() => setSagaForm({...sagaForm, imageUrl: ""})} 
                                                style={{ padding: "4px 8px", fontSize: 11 }}
                                            >
                                                Remove Cover
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: "flex", justifySelf: "end", gap: 8, marginTop: 12 }}>
                                    <button className="admin-btn-secondary" onClick={() => setShowSagaModal(false)}>Cancel</button>
                                    <button className="admin-btn-primary" onClick={saveSaga}>Save Saga</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
